import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { AppContext } from '../containers/appContextContainer';
import { GjeldContainerWeb, GjeldDeleteWeb, GjeldKildeWeb, SoeknadstypeWeb, ValgtWeb } from '../generated-types';
import errorTypes from '../util/gtm/errorTypes';
import restClient from '../util/web-http-client';

export const useGjeld = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['gjeld'],
        queryFn: () => restClient.getGjeld(soeknadtype),
        retry: 1,
    });

    useEffect(() => {
        if (query.error) {
            setError({ errorType: errorTypes.GET_GJELD, errorException: query.error as RequestExceptionWithResponse });
        }
    }, [query.error, setError]);

    return query;
};

export const useUpdateGjeld = (gjeldContainerWeb: GjeldContainerWeb | undefined, successCallback?: () => void) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['updateGjeld'],
        mutationFn: (gjeldContainerWeb: GjeldContainerWeb) => restClient.updateGjeld(gjeldContainerWeb),
        onSuccess: (data: GjeldContainerWeb) => {
            if (gjeldContainerWeb) {
                gjeldContainerWeb = data;
                queryClient.setQueryData(['gjeld'], gjeldContainerWeb);
            }
            if (successCallback) {
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.UPDATE_GJELD, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useDeleteGjeld = (gjeldContainerWeb: GjeldContainerWeb | undefined) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['deleteGjeld'],
        mutationFn: (gjeldDelete: GjeldDeleteWeb) => restClient.deleteGjeld(gjeldDelete),
        onSuccess: () => {
            if (gjeldContainerWeb) {
                gjeldContainerWeb.gjeld.filter((g) => g.kilde !== GjeldKildeWeb.KUNDE);
                gjeldContainerWeb.harGjeld = ValgtWeb.NEI;
                queryClient.setQueryData(['gjeld'], gjeldContainerWeb);
            }
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.DELETE_GJELD, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useSaveInternGjeld = () => {
    return useMutation({
        mutationKey: ['internGjeld'],
        mutationFn: (soeknadId: string) => restClient.saveInternGjeld(soeknadId),
    });
};

export default useGjeld;
